import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import jobService from '../../services/jobService';

// A notice needs its own request (see fetchNotices), so a reopened editor reuses what the last one loaded for a few minutes
// instead of asking ~50 times again. Notices rarely change within a visit.
const CACHE_MS = 5 * 60 * 1000;
let cache = null;    // { at, jobs }
let inflight = null; // the load in progress — a quick close and reopen (or StrictMode's second effect) joins it

const freshJobs = () => (cache && Date.now() - cache.at < CACHE_MS ? cache.jobs : null);

async function fetchNotices() {
    // Every job notice can be turned into an advertisement poster (the poster is a template rendered from job data — same as
    // the detail page — not an uploaded image), so we don't filter by hasPoster. Expired notices get no poster — advertising
    // a closed job helps no one.
    const res = await jobService.getJobs({ sort: 'new', limit: 48, expired: 'exclude' });
    const summaries = res.data || [];
    // The list endpoint returns a summary WITHOUT the poster's education / age texts (posterQualification, posterBosoyShima),
    // so fetch each job's full detail (falling back to the summary if a detail fails — `complete` then says so).
    let complete = true;
    const jobs = await Promise.all(
        summaries.map((j) => jobService.getJob(j.slug || j.id).then((r) => r.data || j).catch(() => { complete = false; return j; })),
    );
    return { jobs, complete };
}

function getNotices() {
    if (!inflight) {
        inflight = fetchNotices()
            .then(({ jobs, complete }) => {
                // keep it only when it is whole: an empty list costs one request, and a list with a failed detail would show
                // blank education / age boxes for the whole cache window instead of being retried on the next opening
                if (jobs.length && complete) cache = { at: Date.now(), jobs };
                return jobs;
            })
            .finally(() => { inflight = null; });
    }
    return inflight;
}

/**
 * What the biggapon editor works from: the current job notices (full ones — they carry the poster fields) and the shop footer.
 * Meant to be used by something that is mounted only while the editor is open — it loads when it mounts.
 *   → { jobs, business, loading }     business = { shopName, businessAddress, phone, shopLogo, … }
 */
export function useBiggaponSource() {
    const user = useAuthStore((s) => s.user);
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);

    // Poster footer = the ACTIVE shop's info. Shop name, address and phone come from the selected office's business profile; the
    // shop phone falls back to the owner's personal number when not set. Falls back to the user (default-office) data entirely
    // if no office is selected yet.
    const business = useMemo(() => (selectedOfficeSpace
        ? {
              ...user,
              shopName: selectedOfficeSpace.businessProfile?.shopName || selectedOfficeSpace.name,
              businessAddress: selectedOfficeSpace.businessProfile?.businessAddress || '',
              phone: selectedOfficeSpace.businessProfile?.shopPhone || user?.phone || '',
              shopLogo: selectedOfficeSpace.businessProfile?.shopLogo || '',
          }
        : user), [user, selectedOfficeSpace]);

    const [jobs, setJobs] = useState(freshJobs); // null = still loading
    useEffect(() => {
        if (jobs) return undefined;
        let cancelled = false;
        getNotices()
            .then((list) => { if (!cancelled) setJobs(list); })
            .catch(() => { if (!cancelled) setJobs([]); });
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return { jobs: jobs ?? [], business, loading: jobs === null };
}
